import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";

const CustomPinInput = ({
  length = 6,
  onComplete,
  onChange,
  disabled = false,
  error = false,
  className = "",
}) => {
  const [values, setValues] = useState(Array(length).fill(""));
  const inputRefs = useRef([]);

  // Focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0] && !disabled) {
      inputRefs.current[0].focus();
    }
  }, [disabled]);

  // Handle input change
  const handleChange = (index, value) => {
    // Only allow single digit
    const digit = value.replace(/[^0-9]/g, "").slice(-1);
    
    const newValues = [...values];
    newValues[index] = digit;
    setValues(newValues);

    // Call onChange if provided
    if (onChange) {
      onChange(newValues.join(""));
    }

    // Move to next input if value entered
    if (digit && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }

    // Check if all inputs are filled
    if (digit && newValues.every(v => v !== "")) {
      if (onComplete) {
        onComplete(newValues.join(""));
      }
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!values[index] && index > 0) {
        // Move to previous input if current is empty
        inputRefs.current[index - 1].focus();
      } else {
        // Clear current input
        const newValues = [...values];
        newValues[index] = "";
        setValues(newValues);
        if (onChange) {
          onChange(newValues.join(""));
        }
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1].focus();
    } else if (e.key === "ArrowRight" && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, length);
    
    if (pastedData) {
      const newValues = [...values];
      pastedData.split("").forEach((digit, i) => {
        if (i < length) {
          newValues[i] = digit;
        }
      });
      setValues(newValues);
      
      if (onChange) {
        onChange(newValues.join(""));
      }
      
      // Focus last filled input or last input
      const lastFilledIndex = newValues.findLastIndex(v => v !== "");
      const focusIndex = lastFilledIndex < length - 1 ? lastFilledIndex + 1 : length - 1;
      inputRefs.current[focusIndex].focus();
      
      // Check if complete
      if (newValues.every(v => v !== "")) {
        if (onComplete) {
          onComplete(newValues.join(""));
        }
      }
    }
  };

  // Clear all inputs (public method)
  const clear = () => {
    setValues(Array(length).fill(""));
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
    if (onChange) {
      onChange("");
    }
  };

  // Expose clear method via ref
  useEffect(() => {
    if (window.pinInputRef) {
      window.pinInputRef.clear = clear;
    }
  }, []);

  return (
    <div className={`flex gap-2 justify-center ${className}`}>
      {values.map((value, index) => (
        <motion.input
          key={index}
          ref={el => inputRefs.current[index] = el}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={value}
          disabled={disabled}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          className={`
            w-12 h-14 sm:w-14 sm:h-16 
            text-center text-xl sm:text-2xl font-semibold
            bg-gray-800/50 border-2 rounded-xl
            transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-blue-500/30
            disabled:opacity-50 disabled:cursor-not-allowed
            ${error 
              ? "border-red-500 bg-red-500/10 text-red-400 animate-shake" 
              : value 
                ? "border-blue-500/50 bg-blue-500/10 text-white" 
                : "border-white/20 text-white hover:border-white/30"
            }
            ${value && !error ? "scale-105" : ""}
          `}
          style={{
            WebkitAppearance: "none",
            MozAppearance: "textfield",
          }}
          whileHover={!disabled ? { scale: 1.05 } : {}}
          whileTap={!disabled ? { scale: 0.95 } : {}}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
        />
      ))}
    </div>
  );
};

export default CustomPinInput;